import React from "react";
import { Tooltip } from "antd";
import { InfoCircleOutlined } from "@ant-design/icons";
import { Localize } from "components/service";
import { timeFormat } from "utils/helper-functions";
import TranslationAiFormat from "../helpers/translation-ai-format-helper";
import TranslationAiLanguage from "../helpers/translation-ai-language-helper";
import TranslationAiPricing from "../helpers/translation-ai-pricing-helper";


const chartColors = {
    blue: '#2f80ed',
    cyan: '#1fb7c9',
    green: '#43c87f',
    magenta: '#e9007f',
    gray: '#7f8b98',
};

const emptyLabel = '\u2014';

const toNumber = (value) => TranslationAiFormat.toNumber(value) || 0;

const takeLast = (items, count = 10) => items.slice(Math.max(items.length - count, 0));

const hasData = (items) => Array.isArray(items) && items.length > 0;

const getPriceValue = (record) => {
    const { value } = TranslationAiPricing.resolvePrice(record);

    return value || 0;
};

const formatChartValue = (value, isPrice = false) => (
    isPrice ?
        TranslationAiFormat.formatPriceValue(value) :
        TranslationAiFormat.formatInteger(value)
);

const getMaxChartValue = (items) => {
    const maxValue = Math.max(...items.map(({ value }) => toNumber(value)), 0);

    return maxValue > 0 ? maxValue : 1;
};

const formatPercent = (value) => `${ Math.round(value) }%`;

const SingleValueChart = ({ label, value, isPrice = false, color = chartColors.cyan }) => (
    <div className="translation-ai-single-value-chart">
        <span className="marker" style={ { backgroundColor: color } } />
        <span className="label">{ label }</span>
        <strong>{ formatChartValue(value, isPrice) }</strong>
    </div>
);

const ChartCard = ({ title, description, children, className = '' }) => (
    <div className={ `translation-ai-chart-card ${ className }` }>
        <div className="translation-ai-chart-header">
            <strong className="translation-ai-chart-title"><Localize>{ title }</Localize></strong>
            { description &&
                <Tooltip title={ <Localize>{ description }</Localize> }>
                    <InfoCircleOutlined />
                </Tooltip>
            }
        </div>
        { description &&
            <p className="translation-ai-chart-description">
                <Localize>{ description }</Localize>
            </p>
        }
        { children }
    </div>
);

const EmptyChart = () => (
    <div className="translation-ai-chart-empty">
        <Localize>GLOBAL.Text_NoData</Localize>
    </div>
);

const AxisGrid = ({ width, height, top, left, right, bottom }) => {
    const innerHeight = height - top - bottom;

    return (
        <>
            { [ 0, 0.5, 1 ].map((part) => {
                const y = top + innerHeight * part;

                return <line key={ part } x1={ left } x2={ width - right } y1={ y } y2={ y } />;
            }) }
        </>
    );
};

export const TranslationAiTrendChart = ({ items, isPrice = false, color = chartColors.cyan }) => {
    if (!hasData(items))
    {
        return <EmptyChart />;
    }

    const data = takeLast(items);
    const maxValue = getMaxChartValue(data);

    if (data.length === 1)
    {
        const [ item ] = data;

        return (
            <SingleValueChart
                label={ item.label }
                value={ item.value }
                isPrice={ isPrice }
                color={ color } />
        );
    }

    const width = 360;
    const height = 128;
    const top = 12;
    const right = 12;
    const bottom = 24;
    const left = 20;
    const innerWidth = width - left - right;
    const innerHeight = height - top - bottom;
    const step = innerWidth / (data.length - 1);
    const points = data.map(({ value }, index) => {
        const numericValue = toNumber(value);
        const x = left + index * step;
        const y = top + innerHeight - (numericValue / maxValue) * innerHeight;

        return { x, y };
    });
    const polylinePoints = points.map(({ x, y }) => `${ x },${ y }`).join(' ');
    const areaPoints = `${ left },${ top + innerHeight } ${ polylinePoints } ${ width - right },${ top + innerHeight }`;

    return (
        <svg className="translation-ai-chart" viewBox={ `0 0 ${ width } ${ height }` } preserveAspectRatio="none">
            <g className="translation-ai-chart-grid">
                <AxisGrid width={ width } height={ height } top={ top } right={ right } bottom={ bottom } left={ left } />
            </g>
            <polygon points={ areaPoints } fill={ color } opacity="0.12" />
            <polyline points={ polylinePoints } fill="none" stroke={ color } strokeWidth="2" />
            { points.map(({ x, y }, index) => (
                <g key={ index }>
                    <circle cx={ x } cy={ y } r="4" fill={ color }>
                        <title>{ `${ data[ index ].label }: ${ formatChartValue(data[ index ].value, isPrice) }` }</title>
                    </circle>
                    <text x={ x } y={ height - 8 } textAnchor="middle">
                        { data[ index ].label }
                    </text>
                </g>
            )) }
        </svg>
    );
};

export const TranslationAiSplitBarChart = ({ input, output }) => {
    const total = input + output;
    const inputPercent = total > 0 ? (input / total) * 100 : 0;
    const outputPercent = total > 0 ? (output / total) * 100 : 0;
    const inputLabel = `${ formatChartValue(input) } (${ formatPercent(inputPercent) })`;
    const outputLabel = `${ formatChartValue(output) } (${ formatPercent(outputPercent) })`;

    if (total === 0)
    {
        return <EmptyChart />;
    }

    return (
        <div className="translation-ai-split-chart">
            <div className="translation-ai-split-track">
                <span
                    className="input"
                    style={ { width: `${ inputPercent }%` } }
                    title={ `Input tokens: ${ inputLabel }` } />
                <span
                    className="output"
                    style={ { width: `${ outputPercent }%` } }
                    title={ `Output tokens: ${ outputLabel }` } />
            </div>
            <div className="translation-ai-split-values">
                <span>
                    <i style={ { backgroundColor: chartColors.cyan } } />
                    <Localize>TABLES.Column_Title_PromptTokens</Localize>
                    <strong>{ inputLabel }</strong>
                </span>
                <span>
                    <i style={ { backgroundColor: chartColors.blue } } />
                    <Localize>TABLES.Column_Title_CompletionTokens</Localize>
                    <strong>{ outputLabel }</strong>
                </span>
            </div>
        </div>
    );
};

export const TranslationAiHorizontalBarChart = ({ items, isPrice = false, limit = 20 }) => {
    if (!hasData(items))
    {
        return <EmptyChart />;
    }

    const data = items.slice(0, limit);
    const maxValue = getMaxChartValue(data);

    if (data.length === 1)
    {
        const [ item ] = data;

        return (
            <div className="translation-ai-horizontal-single">
                <div className="translation-ai-horizontal-single-item">
                    <span className="label">{ item.label }</span>
                    <strong>{ formatChartValue(item.value, isPrice) }</strong>
                    <span className="share">100%</span>
                </div>
            </div>
        );
    }

    return (
        <div className="translation-ai-horizontal-chart">
            { data.map(({ label, value, color = chartColors.cyan }) => {
                const numericValue = toNumber(value);

                return (
                    <div className="translation-ai-horizontal-row" key={ label }>
                        <span className="label">{ label }</span>
                        <span className="track">
                            <i
                                style={ {
                                    width: `${ Math.max((numericValue / maxValue) * 100, numericValue > 0 ? 2 : 0) }%`,
                                    backgroundColor: color,
                                } } />
                        </span>
                        <strong>{ formatChartValue(value, isPrice) }</strong>
                    </div>
                );
            }) }
        </div>
    );
};

const groupBy = (records, field) => (
    records.reduce((acc, record) => {
        const key = record?.[ field ] || emptyLabel;
        acc[ key ] = (acc[ key ] || 0) + 1;

        return acc;
    }, {})
);

const groupCostBy = (records, field) => (
    records.reduce((acc, record) => {
        const key = record?.[ field ] || emptyLabel;
        acc[ key ] = (acc[ key ] || 0) + getPriceValue(record);

        return acc;
    }, {})
);

const groupLanguages = (records, languageMap = {}) => (
    records.reduce((acc, record) => {
        const key = TranslationAiLanguage.getLanguageLabel(record?.lang_target, languageMap) || emptyLabel;
        acc[ key ] = (acc[ key ] || 0) + 1;

        return acc;
    }, {})
);

const groupedItems = (grouped, color) => (
    Object.entries(grouped)
        .map(([ label, value ]) => ({ label, value, color }))
        .sort((a, b) => b.value - a.value)
        .slice(0, 20)
);

const hasBreakdown = (items) => items.length > 1;

export const TranslationAiUsageCharts = ({ records = [] }) => {
    const sortedRecords = [ ...records ].reverse();
    const chartRecords = sortedRecords.map((record) => ({
        ...record,
        label: record?.date_at ? timeFormat(record.date_at, 'DD.MM') : emptyLabel,
    }));
    const inputTokens = records.reduce((acc, record) => acc + toNumber(record.prompt_tokens), 0);
    const outputTokens = records.reduce((acc, record) => acc + toNumber(record.completion_tokens), 0);

    return (
        <div className="translation-ai-charts">
            <ChartCard
                title="TABLES.Chart_Title_RequestsByDay"
                description="TABLES.Chart_Description_RequestsByDay">
                <TranslationAiTrendChart
                    items={ chartRecords.map((record) => ({
                        label: record.label,
                        value: toNumber(record.requests_count),
                        color: chartColors.cyan,
                    })) }
                    color={ chartColors.cyan } />
            </ChartCard>
            <ChartCard
                title="TABLES.Chart_Title_TokenVolume"
                description="TABLES.Chart_Description_TokenVolume">
                <TranslationAiSplitBarChart input={ inputTokens } output={ outputTokens } />
            </ChartCard>
            <ChartCard
                title="TABLES.Chart_Title_CostTrend"
                description="TABLES.Chart_Description_CostTrend">
                <TranslationAiTrendChart
                    items={ chartRecords.map((record) => ({
                        label: record.label,
                        value: toNumber(record.total_tokens_price),
                    })) }
                    isPrice
                    color={ chartColors.magenta } />
            </ChartCard>
        </div>
    );
};

export const TranslationAiRequestsCharts = ({ records = [], languageMap = {} }) => {
    const byModel = groupedItems(groupBy(records, 'model'), chartColors.blue);
    const byLanguage = groupedItems(groupLanguages(records, languageMap), chartColors.green);
    const costByModel = groupedItems(groupCostBy(records, 'model'), chartColors.magenta);

    return (
        <div className="translation-ai-charts">
            { hasBreakdown(byModel) &&
                <ChartCard
                    title="TABLES.Chart_Title_RequestsByModel"
                    description="TABLES.Chart_Description_RequestsByModel">
                    <TranslationAiHorizontalBarChart items={ byModel } />
                </ChartCard>
            }
            <ChartCard
                title="TABLES.Chart_Title_TargetLanguages"
                description="TABLES.Chart_Description_TargetLanguages">
                <TranslationAiHorizontalBarChart items={ byLanguage } />
            </ChartCard>
            { hasBreakdown(costByModel) &&
                <ChartCard
                    title="TABLES.Chart_Title_CostByModel"
                    description="TABLES.Chart_Description_CostByModel">
                    <TranslationAiHorizontalBarChart items={ costByModel } isPrice />
                </ChartCard>
            }
        </div>
    );
};

const TranslationAiCharts = {
    Usage: TranslationAiUsageCharts,
    Requests: TranslationAiRequestsCharts,
};

export default TranslationAiCharts;
